import React, { useEffect } from 'react'
import { createBrowserRouter, RouterProvider, Outlet } from 'react-router'
import { I18nextProvider } from 'react-i18next'
import i18n from '../i18n'
import { ThemeProvider } from 'styled-components'
import { appTheme } from './config/styled'
import Session from '../javascripts/layouts/v2/Session'
import { session_path } from '../routes'
import TagManager from 'react-gtm-module'
import loadRouteGroup from './config/Routes'
import RouteErrorBoundary from './config/Routes/RouteErrorBoundary'
import 'css/tailwind.css'

export const SessionContext = React.createContext<Session>(null)

export const setCsrfMetaTags = (session: Session) => {
  if (!session.csrf_token) return

  const existingParam = document.querySelector('meta[name="csrf-param"]')
  const existingToken = document.querySelector('meta[name="csrf-token"]')
  if (existingParam) existingParam.remove()
  if (existingToken) existingToken.remove()

  const csrfParam = document.createElement('meta')
  csrfParam.name = 'csrf-param'
  csrfParam.content = session.csrf_param
  document.head.appendChild(csrfParam)

  const csrfToken = document.createElement('meta')
  csrfToken.name = 'csrf-token'
  csrfToken.content = session.csrf_token
  document.head.appendChild(csrfToken)
}

const updateDataLayer = (session: Session) => {
  const userId = session.user?.id

  if (!userId) { return }

  TagManager.dataLayer({ dataLayer: { uid: userId } })
}

// NOTE: react-router v7 の Vite プラグインがページコンポーネントを withComponentProps でラップし、
// useLoaderData() を呼ぶため、createBrowserRouter による data router が必要。
// BrowserRouter や useRoutes では routes に id が付与されず useLoaderData がエラーになる。
// NOTE: 各ルートを lazy で遅延読み込みしているため、初回描画時はルーターが未初期化状態になる。
// その間に描画する HydrateFallback が無いと react-router が警告を出すため、空要素を明示的に渡す。
const HydrateFallback: React.FC = () => null

const RootLayout: React.FC = () => {
  const [session, setSession] = React.useState<Session>(null)

  // NOTE: ワーカーの各コンポーネントでセッション情報を扱えるようにするため、セッションAPIを実行しています。
  // 詳細 → https://github.com/crowdtech/crowdtech/pull/27412#issuecomment-2154261434
  useEffect(() => {
    void fetch(session_path())
      .then(response => response.json())
      .then((json: Session) => {
        setCsrfMetaTags(json)
        setSession(json)
        updateDataLayer(json)
      })
  }, [])

  return (
    <I18nextProvider i18n={i18n}>
      <SessionContext.Provider value={session}>
        <ThemeProvider theme={appTheme}>
          <Outlet />
        </ThemeProvider>
      </SessionContext.Provider>
    </I18nextProvider>
  )
}

const App: React.FC = () => {
  const [router, setRouter] = React.useState<ReturnType<typeof createBrowserRouter> | null>(null)

  useEffect(() => {
    loadRouteGroup()
      .then(({ group, routes }) => {
        const ErrorBoundary: React.FC = () => <RouteErrorBoundary loadedGroup={group} />

        setRouter(createBrowserRouter([
          {
            Component: RootLayout,
            HydrateFallback,
            ErrorBoundary,
            children: routes,
          }
        ]))
      })
      // NOTE: この Promise は router の外側で解決されるため、失敗しても RouteErrorBoundary では
      // 捕捉できない。他のエラーと同様に画面には何も描画しない（router が null のままになる）が、
      // 未処理の rejection にしないよう明示的に捕捉する。
      .catch(() => {})
  }, [])

  if (!router) return null

  return <RouterProvider router={router} />
}

export default App
